// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Wraps the `/api/v1/role-assignments` family — the only way to actually
 * grant or revoke a role in IAM (ADR-0026: a user's role is a role
 * assignment, not a field on the user). Lives in `shared/` per ADR-0014;
 * `get`/`post`/`del` stay imported from the real `services/api` for their
 * token/401 handling.
 */

import { get, post, del } from '../../helios/services/api';
import { ENDPOINTS } from '../utils/constants';
import type { RoleAssignmentDTO } from '../types/iam';

export const getUserRoleAssignments = async (userId: string): Promise<RoleAssignmentDTO[]> => {
  return get<RoleAssignmentDTO[]>(ENDPOINTS.ROLE_ASSIGNMENTS_BY_USER(userId));
};

export interface CreateRoleAssignmentParams {
  userId: string;
  roleId: string;
  resourceId: string;
  /** Forwarded as-is when replacing a time-limited assignment, so swapping a
   *  user's role (or site) doesn't silently turn their expiring grant into
   *  an indefinite one. */
  expiresAt?: string;
}

export const createRoleAssignment = async (
  params: CreateRoleAssignmentParams
): Promise<RoleAssignmentDTO> => {
  return post<RoleAssignmentDTO>(ENDPOINTS.ROLE_ASSIGNMENTS, {
    userId: params.userId,
    roleId: params.roleId,
    resourceId: params.resourceId,
    ...(params.expiresAt ? { expiresAt: params.expiresAt } : {}),
  });
};

export const deleteRoleAssignment = async (assignmentId: string): Promise<void> => {
  await del<null>(ENDPOINTS.ROLE_ASSIGNMENT_BY_ID(assignmentId));
};
